<template>
  <v-dialog v-model="isDialogActive" max-width="700" z-index="100001" transition="dialog-bottom-transition" class="modal-dialog">
    <template v-slot:default="{ isActive }">
      <v-card :class="[props.noPadding ? 'pa-0' : 'modal-padding']" rounded="lg" :color="props.color">
        <!-- Close Icon -->
        <span :class="['d-flex flex-column align-center position-fixed top-0 right-0', 'mt-3 mr-4']" style="z-index: 100002">
          <v-icon
          class="align-self-md-end pa-4 rounded-circle"
          :color="props.closeIcon ? 'white' : 'primary'"
          :style="props.closeIcon ? 'background: rgb(var(--v-theme-primary)) !important;' : 'background: white !important;'"
          size="22px"
          @click="handleClose">
          mdi-close
        </v-icon>
      </span>
      <!-- Modal Header -->
      <v-card v-if="title || description" class="d-flex justify-space-between flex-row ma-0 pa-0" flat variant="text" style="min-height: fit-content">
        <span class="d-flex align-baseline flex-column ga-0 ma-0 pa-0">
          <!-- <v-icon class="ma-0 mr-2" style="opacity: 1" color="primary" size="18px">mdi-information-slab-circle</v-icon> -->
          <h1 class="ma-0 pa-0 ps-1 text-start modal_h1_title lang_translate">
            {{ title }}
          </h1>
          <p v-if="description" class="ma-0 py-0 lang_translate" style="font-size: 16px; padding-left: 1px; font-style: italic; color: rgb(var(--v-theme-primary)) !important">
            {{ description }}
          </p>
        </span>
      </v-card>
      <div class="ma-0 pa-0">
        <v-card class="ma-0 pa-0" variant="text" rounded="lg">
          <!-- Dynamic Component Loader -->
          <component ref="dynamicComponentRef" :is="pageComponent" v-bind="pageComponentProps" @close="handleClose" @action="handleAction" />
        </v-card>
      </div>
    </v-card>
  </template>
</v-dialog>
</template>

<script lang="ts" setup>
import { useDisplay } from 'vuetify';
import { useModalStore } from '@/store/modalStore';
import { computed, ref, watch } from 'vue';

const modalStore = useModalStore(); // Access the modal store
const dynamicComponentRef = ref(); // Reference to the dynamic component
const { smAndDown } = useDisplay(); // `useDisplay` to check screen size
// const dialogTransition = computed(() => smAndDown.value ? 'dialog-bottom-transition' : undefined); // Determine transition based on screen size

// Props (for existing click functionality)
const props = defineProps({
  title: String,
  description: String,
  pageComponent: Object,
  isVisible: Boolean,
  pageComponentProps: Object,
  boxid: Number,
  color: {
    type: String,
    default: 'white',
  },
  closeIcon: {
    type: Boolean,
    default: false,
  },
  noPadding: Boolean,
});

// Emit events
const emit = defineEmits(['update:modelValue', 'action']);

// Reactive state for existing click functionality
const isDialogActive = ref(props.isVisible);

// Watch for visibility prop changes (existing functionality)
watch(
() => props.isVisible,
(newVal) => {
  isDialogActive.value = newVal;
},
{ immediate: true } // ✅ Ensure it runs immediately
);

// Watch for store-based modal trigger
watch(
() => modalStore.isVisible,
(newVal) => {
  isDialogActive.value = newVal;
}
);

// ✅ NEW — detect backdrop click close and sync the store
watch(isDialogActive, (val) => {
  if (!val && modalStore.isVisible) {
    console.log('[ModalPopup] Backdrop close detected — resetting store');
    modalStore.closeModal();
  }
});

// Handle close action (works for both store and click-triggered modal)
const handleClose = async () => {
  const exposed = dynamicComponentRef.value;
  if (exposed && typeof exposed.onClose === 'function') {
    await exposed.onClose();
  }
  
  isDialogActive.value = false;
  modalStore.closeModal();
  emit('update:modelValue', false);
};

// Handle actions emitted by the dynamic component
const handleAction = (actionData: any) => {
  emit('action', actionData); // Pass action data to the parent
};
</script>

<style lang="scss">
.modal-padding {
  padding: 16px;
  gap: 8px;
  @media (min-width: $small-screen) {
    padding: 16px;
    gap: 16px;
  }
  @media (min-width: $mobile-screen) {
    padding: 24px;
    gap: 16px;
  }
}
// H1 title style for the modal
.modal_h1_title {
  text-transform: uppercase;
  font-weight: 500;
  font-style: italic;
  font-family: $mongoose-font;
  font-size: 2rem;
  color: rgb(var(--v-theme-primary));
  line-height: 32px;
}

.v-dialog.modal-dialog {
  .v-overlay__content {
    margin: 0 !important;
    width: calc(100% - 16px);
    
    @media (min-width: $small-screen) {
      width: calc(100% - 32px);
    }
    
    @media (min-width: $mobile-screen) {
      width: calc(100% - 48px);
    }
  }
}
</style>